import { useCallback, useEffect, useRef, useState } from "react";
import { messagingReady, getToken } from "../firebase";

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// A foregrounded PWA can fire visibilitychange/focus many times a minute. Checking
// more often than this buys nothing — the token is cached locally by the SDK.
const DEFAULT_REVALIDATE_INTERVAL_MS = 5 * 60 * 1000;

const FCM_SW_URL = "/firebase-messaging-sw.js";
const FCM_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

function readPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission || "default";
}

// Safari does not accept a custom-scope worker here; let the SDK use its own.
function prefersOwnServiceWorker() {
  if (!("serviceWorker" in navigator)) return false;
  return !/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

const useMessages = (shouldInitialize, options = {}) => {
  const { autoRequest = true, revalidateIntervalMs = DEFAULT_REVALIDATE_INTERVAL_MS } = options;
  const [token, setToken] = useState(null);
  const [message] = useState(null);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState(readPermission);
  // Bumped on every completed check so useFcmTokenSync knows to confirm the
  // server still holds the token, even when the token itself is unchanged.
  const [revalidation, setRevalidation] = useState(0);

  const registrationRef = useRef(null);
  const lastCheckedAtRef = useRef(0);
  const inFlightRef = useRef(null);

  const resolveRegistration = useCallback(async () => {
    if (!prefersOwnServiceWorker()) return undefined;
    if (registrationRef.current) return registrationRef.current;

    // One-time cleanup of the legacy root-scoped worker.
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations
        .filter((reg) => {
          const scriptUrl =
            reg.active?.scriptURL || reg.waiting?.scriptURL || reg.installing?.scriptURL || "";
          return scriptUrl.includes(FCM_SW_URL) && reg.scope === `${window.location.origin}/`;
        })
        .map((reg) => reg.unregister())
    );

    const registration = await navigator.serviceWorker.register(FCM_SW_URL, {
      scope: FCM_SW_SCOPE,
    });
    await registration.update();
    registrationRef.current = registration;
    return registration;
  }, []);

  const requestToken = useCallback(
    async ({ prompt = true } = {}) => {
      // Collapse concurrent checks (mount + foreground can land together).
      if (inFlightRef.current) return inFlightRef.current;

      const run = (async () => {
        setError(null);

        const messaging = await messagingReady;
        if (!messaging) {
          const nextError = "Firebase messaging is not supported in this environment.";
          setError(nextError);
          return { token: "", error: nextError };
        }

        if (typeof Notification === "undefined") {
          const nextError = "Browser notifications are not supported in this environment.";
          setError(nextError);
          return { token: "", error: nextError };
        }

        try {
          let currentPermission = Notification.permission;

          if (currentPermission !== "granted" && prompt) {
            currentPermission = await Notification.requestPermission();
          }

          setPermission(currentPermission);

          if (currentPermission !== "granted") {
            const nextError = `Notification permission not granted: ${currentPermission}`;
            setError(nextError);
            return { token: "", error: nextError };
          }

          const registration = await resolveRegistration();
          const currentToken = await getToken(
            messaging,
            registration ? { vapidKey, serviceWorkerRegistration: registration } : { vapidKey }
          );

          if (currentToken) {
            lastCheckedAtRef.current = Date.now();
            setToken(currentToken);
            setRevalidation((count) => count + 1);
            return { token: currentToken, error: "" };
          }

          const nextError = "No registration token available";
          console.warn(nextError);
          setError(nextError);
          return { token: "", error: nextError };
        } catch (err) {
          // A failed check must not be terminal — the next foreground retries.
          registrationRef.current = null;
          const nextError = err?.message || "An error occurred while retrieving token.";
          console.warn("An error occurred while retrieving token.", err);
          setError(nextError);
          return { token: "", error: nextError };
        }
      })();

      inFlightRef.current = run;
      try {
        return await run;
      } finally {
        inFlightRef.current = null;
      }
    },
    [resolveRegistration]
  );

  useEffect(() => {
    if (!shouldInitialize) {
      setToken(null);
      setError(null);
      return undefined;
    }

    const shouldCheckSilently = () => readPermission() === "granted";

    if (autoRequest || shouldCheckSilently()) {
      requestToken({ prompt: autoRequest });
    } else {
      // Nothing to request, but record why. This used to be a silent no-op, which
      // is how revoked permissions went unnoticed for months.
      const current = readPermission();
      setPermission(current);
      setError(`Notification permission not granted: ${current}`);
    }

    // Self-heal: the token can be revoked by the OS, rotated by the browser, or
    // pruned server side while the app is closed. Re-check whenever it reopens.
    const revalidate = () => {
      if (document.visibilityState !== "visible") return;
      setPermission(readPermission());
      if (!shouldCheckSilently()) return;
      if (Date.now() - lastCheckedAtRef.current < revalidateIntervalMs) return;
      requestToken({ prompt: false });
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [autoRequest, requestToken, revalidateIntervalMs, shouldInitialize]);

  return { token, message, error, permission, revalidation, requestToken };
};

export default useMessages;
